import { Button, Dropdown, Tooltip, message } from 'antd'
import { CloudOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import UserDropdown from '@/pages/layout/modules/UserDropdown'
import { useSubscriptionStore } from '@/stores/subscriptionStore'
import { isThesisCloudAuth } from '@/lib/thesisApi'
import {
  getCloudProjectByLocal,
  putCloudByLocal,
  putCloudProject,
} from '@/lib/thesisCloudApi'
import { applyThesisCloudPayload, collectThesisCloudPayload } from '@/lib/thesisCloudBundle'
import { getCloudBinding, setCloudBinding } from '@/lib/thesisCloudMap'

/**
 * 工作台右上精简：云同步 + 账号。
 * 导出 / 帮助 / 主题等放进账号弹窗，避免一排小图标。
 */
export default function ThesisWorkspaceChrome() {
  const { novelId } = useParams()
  const token = useSubscriptionStore((s) => s.token)
  const cloudOn = !!(novelId && token && isThesisCloudAuth())
  const [busy, setBusy] = useState(false)
  const [ver, setVer] = useState<number | null>(null)

  const refreshMeta = useCallback(async () => {
    if (!novelId || !token || !isThesisCloudAuth()) return
    const bound = getCloudBinding(novelId)
    if (bound) setVer(bound.clientVersion)
    const r = await getCloudProjectByLocal(novelId)
    if (r.ok) {
      setVer(r.data.clientVersion)
      setCloudBinding(novelId, {
        projectId: r.data.id,
        clientVersion: r.data.clientVersion,
        updatedAt: r.data.updatedAt,
      })
    }
  }, [novelId, token])

  useEffect(() => {
    void refreshMeta()
  }, [refreshMeta])

  const push = async () => {
    if (!novelId) return
    setBusy(true)
    try {
      const { title, payload } = collectThesisCloudPayload(novelId)
      const bound = getCloudBinding(novelId)
      const clientVersion = bound?.clientVersion ?? 0
      const r = bound?.projectId
        ? await putCloudProject(bound.projectId, {
            clientVersion,
            title,
            payload,
            forceOverwrite: false,
          })
        : await putCloudByLocal(novelId, {
            clientVersion: clientVersion || 0,
            title,
            payload,
            forceOverwrite: false,
          })
      if (!r.ok) {
        message.error(r.error || '保存失败')
        return
      }
      setCloudBinding(novelId, {
        projectId: r.data.id,
        clientVersion: r.data.clientVersion,
        updatedAt: r.data.updatedAt,
      })
      setVer(r.data.clientVersion)
      message.success('已保存到云端')
    } finally {
      setBusy(false)
    }
  }

  const pull = async () => {
    if (!novelId) return
    setBusy(true)
    try {
      const r = await getCloudProjectByLocal(novelId)
      if (!r.ok) {
        if (r.status === 404) {
          message.info('云端尚无，请先保存')
          return
        }
        message.error(r.error || '拉取失败')
        return
      }
      const applied = await applyThesisCloudPayload(novelId, r.data.payload)
      if (!applied.ok) {
        message.error(applied.error)
        return
      }
      setCloudBinding(novelId, {
        projectId: r.data.id,
        clientVersion: r.data.clientVersion,
        updatedAt: r.data.updatedAt,
      })
      setVer(r.data.clientVersion)
      message.success('已从云端拉取')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-0.5 shrink-0">
      {cloudOn ? (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'meta',
                label: ver != null ? `云端 v${ver}` : '尚未上传',
                disabled: true,
              },
              { key: 'pull', label: '从云端拉取' },
              { key: 'push', label: '保存到云端' },
            ],
            onClick: ({ key }) => {
              if (key === 'pull') void pull()
              if (key === 'push') void push()
            },
          }}
        >
          <Tooltip title="云同步">
            <Button size="small" type="text" loading={busy} icon={<CloudOutlined />} />
          </Tooltip>
        </Dropdown>
      ) : null}
      <UserDropdown placement="bottomRight" workspace />
    </div>
  )
}
